import { Link } from "react-router-dom";
import { ArrowRight, ExternalLink } from "lucide-react";
import { getRouteState } from "@/lib/navigation";
import { trackCanvasCustomQuote } from "@/lib/analytics";

/**
 * Next-step copy + primary / secondary CTAs + trust copy for the canvas
 * product page. Pure rendering — parent owns all state and the checkout handler.
 */
export default function CanvasPurchaseCTAs({
  location,
  handle,
  nextStepCopy,
  isCustomSize,
  isVariantUnavailable,
  isPriceOnRequest,
  checkoutLoading,
  purchaseLabel,
  customQuoteHref,
  onCheckout,
  selectedFrame,
}) {
  return (
    <>
      <div className="mt-8" data-testid="canvas-next-step-panel">
        <p className="text-[11px] tracking-[0.18em] uppercase font-semibold text-brand-gold">
          Next Step
        </p>
        <p className="mt-2 text-[13px] text-brand-text-secondary leading-relaxed" data-testid="next-step-copy">
          {nextStepCopy}
        </p>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row gap-3" data-testid="product-ctas">
        {renderPrimaryCTA({
          isCustomSize,
          isVariantUnavailable,
          isPriceOnRequest,
          checkoutLoading,
          purchaseLabel,
          customQuoteHref,
          location,
          handle,
          onCheckout,
        })}
        <Link
          to={customQuoteHref}
          state={getRouteState(location)}
          className="inline-flex items-center justify-center gap-2 border border-brand-gold/60 text-brand-gold hover:bg-brand-gold/10 transition-colors duration-300 rounded-sm px-7 py-3.5 font-medium text-[13px] tracking-wide"
          data-testid="request-custom-cta"
        >
          {isCustomSize ? "Send Size Details" : "Request Custom Size"}
          <ArrowRight size={14} />
        </Link>
      </div>

      <p
        className="mt-5 text-[11px] text-brand-text-secondary/40 leading-snug"
        data-testid="canvas-purchase-trust-copy"
      >
        UK printed &amp; shipped. Archival inks on premium stretched canvas.
        {!isCustomSize && " Secure Shopify product page and checkout."}
        {selectedFrame !== "none" && " Frame pricing is included in the selector above."}
      </p>
    </>
  );
}

function renderPrimaryCTA({
  isCustomSize,
  isVariantUnavailable,
  isPriceOnRequest,
  checkoutLoading,
  purchaseLabel,
  customQuoteHref,
  location,
  handle,
  onCheckout,
}) {
  if (isCustomSize) {
    return (
      <Link
        to={customQuoteHref}
        state={getRouteState(location)}
        className="inline-flex items-center justify-center gap-2 bg-brand-gold text-brand-bg hover:bg-brand-gold-hover transition-colors duration-300 rounded-sm px-7 py-3.5 font-medium text-[13px] tracking-wide"
        data-testid="buy-on-store-cta"
        onClick={() => trackCanvasCustomQuote(handle)}
      >
        Request Custom Size Quote
        <ArrowRight size={14} />
      </Link>
    );
  }
  if (isVariantUnavailable) {
    return (
      <span
        className="inline-flex items-center justify-center gap-2 bg-brand-text-secondary/15 text-brand-text-secondary/50 rounded-sm px-7 py-3.5 font-medium text-[13px] tracking-wide cursor-not-allowed"
        data-testid="buy-on-store-cta"
        data-unavailable="true"
      >
        Currently Unavailable
        <ExternalLink size={14} />
      </span>
    );
  }
  const btnCls = checkoutLoading
    ? "bg-brand-gold/60 text-brand-bg/70 cursor-wait"
    : "bg-brand-gold text-brand-bg hover:bg-brand-gold-hover";
  return (
    <button
      type="button"
      onClick={onCheckout}
      disabled={checkoutLoading}
      className={`inline-flex items-center justify-center gap-2 rounded-sm px-7 py-3.5 font-medium text-[13px] tracking-wide transition-colors duration-300 ${btnCls}`}
      data-testid="buy-on-store-cta"
    >
      {checkoutLoading ? (
        <>
          <span className="inline-block w-4 h-4 border-2 border-brand-bg/30 border-t-brand-bg rounded-full animate-spin" />
          Preparing checkout…
        </>
      ) : (
        <>
          {isPriceOnRequest ? "Configure on Store" : purchaseLabel}
          <ExternalLink size={14} />
        </>
      )}
    </button>
  );
}
